import React from "react";

// fetchAnnouncement.js - API functions for announcement management

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Helper function to handle response parsing
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  
  if (!response.ok) {
    let errorMessage;
    
    if (contentType && contentType.includes('application/json')) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch (jsonError) {
        errorMessage = `Request failed with status ${response.status}`;
      }
    } else {
      // Handle non-JSON error responses (HTML error pages, plain text, etc.)
      try {
        const textResponse = await response.text();
        
        // Check for common backend issues and provide helpful messages
        if (textResponse.includes('object cycle was detected')) {
          errorMessage = 'Backend Error: Circular reference detected. The API needs to use DTOs or configure JSON serialization to handle object cycles.';
        } else if (textResponse.includes('JsonException')) {
          errorMessage = 'Backend Error: JSON serialization failed. Check API response format.';
        } else {
          errorMessage = `Server error (${response.status}): ${textResponse.substring(0, 150)}...`;
        }
      } catch (textError) {
        errorMessage = `Request failed with status ${response.status}`;
      }
    }
    
    throw new Error(errorMessage);
  }

  // Parse successful response as JSON
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  } else {
    throw new Error('Expected JSON response but received different content type');
  }
};

// GET - Fetch announcements by building ID
export const getListAnnouncementByBuildingId = async (buildingId) => {
  try {
    const response = await fetch(`${API_URL}/api/Announcement/GetAnnouncement?buildingId=${buildingId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    throw error;
  }
};

// POST - Add new announcement
export const addAnnouncement = async (announcementData) => {
  try {
    const response = await fetch(`${API_URL}/api/Announcement/AddAnnouncement`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(announcementData)
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    throw error;
  }
};

// POST - Update existing announcement
export const updateAnnouncement = async (announcementData) => {
  try {
    const response = await fetch(`${API_URL}/api/Announcement/UpdateAnnouncement`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(announcementData)
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    throw error;
  }
};

// DELETE - Delete announcement by ID
export const deleteAnnouncement = async (announcementId, userId = null) => {
  try {
    const url = userId 
      ? `${API_URL}/api/Announcement/DeleteAnnouncement/${announcementId}?userId=${userId}`
      : `${API_URL}/api/Announcement/DeleteAnnouncement/${announcementId}`;
      
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    throw error;
  }
};

// Alternative POST delete method (if you prefer the POST approach)
export const deleteAnnouncementPost = async (announcementId, userId = null) => {
  try {
    const response = await fetch(`${API_URL}/api/Announcement/DeleteAnnouncement`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        announcementId: announcementId,
        userId: userId
      })
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    throw error;
  }
};